import { formatCrawlAt } from '@components/SitemapReport/schedule'
import { Modal } from '@lib/components'
import { Radio } from '@lib/components/Radio'
import type { ScheduleDto } from '@octopus-do/octopus-api-client'
import { Button, Loader } from '@octopus-do/octopus-storybook'
import type React from 'react'
import { Fragment } from 'react'
import { useParams } from 'react-router-dom'

import type { ModalProps } from '@/types'
import { DaysSelect, HourSelect } from './components'
import { useScheduleCrawl } from './hooks/useScheduleCrawl'
import css from './styles.module.scss'
import { nextRun, SCHEDULE_OPTIONS } from './utils'

interface Props extends ModalProps {
  initial?: ScheduleDto | null
  onSaved?: () => void
}

export const ScheduleCrawl: React.FC<Props> = ({
  initial,
  onClose,
  onSaved,
}) => {
  const { uuid } = useParams<{ uuid: string }>()

  // The screen behind the dialog holds the schedule it was opened with, and
  // that copy is what the dialog is handed next time. Saving has to hand it
  // back, or the second opening starts from the setting that was just replaced.
  const done = () => {
    onSaved?.()
    onClose()
  }
  const {
    changed,
    draft,
    error,
    loading,
    save,
    saving,
    setHour,
    setSchedule,
    toggleDay,
  } = useScheduleCrawl(uuid, done, initial)

  const due = draft && nextRun(draft)

  return (
    <Modal
      classnames={{ content: css.modal, body: css.body }}
      onClose={onClose}
    >
      <div className={css.container}>
        <div className={css.title}>Schedule crawl</div>
        <div className={css.subtitle}>
          Automatically crawl your sitemap on a recurring schedule.
        </div>

        {loading && <Loader color="gray" threeDots />}

        {draft && (
          <div className={css.options}>
            {SCHEDULE_OPTIONS.map(({ label, value }) => (
              <Fragment key={value}>
                <div className={css.divider} />

                <div className={css.option}>
                  <Radio
                    name="schedule"
                    label={label}
                    size="large"
                    initialValue={value}
                    defaultChecked={draft.schedule === value}
                    classnames={{ container: css.radio, input: css.radioInput }}
                    onChange={(() => setSchedule(value)) as never}
                  />

                  {draft.schedule === value && value !== 'off' && (
                    <div className={css.controls}>
                      {value !== 'daily' && (
                        <DaysSelect
                          schedule={draft.schedule}
                          value={draft.days}
                          onToggle={toggleDay}
                        />
                      )}
                      <HourSelect value={draft.hour} onChange={setHour} />
                    </div>
                  )}
                </div>
              </Fragment>
            ))}

            <div className={css.divider} />
          </div>
        )}

        {due && (
          <div className={css.next}>Next crawl: {formatCrawlAt(due)}</div>
        )}

        {error && <div className={css.error}>{error}</div>}
      </div>

      <div className={css.footer}>
        <Button
          className={css.button}
          label="Set schedule"
          disabled={!changed}
          loading={saving}
          onClick={save}
        />
        <Button
          className={css.button}
          priority="secondary"
          label="Cancel"
          onClick={onClose}
        />
      </div>
    </Modal>
  )
}
